import { type DependencyList, useEffect } from "react";

import { JobStatusEnum, WorkflowActivityStatusEnum } from "@/graphql.ts";
import { SubscriptionTypeEnum } from "@/modules/notifications/utils/types.ts";
import { WorkflowPubSubActions } from "@/modules/workflow/events/pubsub-actions.ts";
import { useWorkflowEditorStore } from "@/modules/workflow/stores/workflow-editor-store.ts";
import type { ExtendedJobStatusEnum, PreviewNodeData } from "@/modules/workflow/utils/types.ts";
import { webSocketClient } from "@/tools.ts";

export const useWorkflowEditorWebSocket = () => {
  const workflowEventId = useWorkflowEditorStore((state) => state.workflowEvent.id);
  const updateNodesMetadata = useWorkflowEditorStore((state) => state.updateNodesMetadata);
  const updateWorkflowEvent = useWorkflowEditorStore((state) => state.updateWorkflowEvent);
  const refreshWorkflowEventDetails = useWorkflowEditorStore((state) => state.refreshWorkflowEventDetails);

  useEffect(() => {
    let subscriptionId: string | null = null;

    if (workflowEventId) {
      subscriptionId = webSocketClient.subscribe({
        subscription: { eventType: SubscriptionTypeEnum.WorkflowActivity, variables: { workflowEventId } },
        onMessage: (message) => {
          if (message.workflowEventId === workflowEventId) {
            if (message.activity) {
              updateNodesMetadata([{ nodeId: message.activity.identifier, data: { status: message.activity.status } }]);

              if (message.activity.status === WorkflowActivityStatusEnum.Completed) {
                PubSub.publish(WorkflowPubSubActions.RELOAD_OUTPUT, message.activity.identifier);
              }
            }

            if (message.workflow) {
              updateWorkflowEvent({
                extendedExecutionStatus: message.workflow.status as unknown as ExtendedJobStatusEnum,
                executionUser: message.workflow.user,
              });

              refreshWorkflowEventDetails();

              if (message.workflow.status === JobStatusEnum.Completed) {
                PubSub.publish(WorkflowPubSubActions.RELOAD_OUTPUT, null);
              }
            }
          }
        },
      });
    }

    return () => {
      if (subscriptionId != undefined) {
        webSocketClient.unsubscribe(subscriptionId);
      }
    };
  }, [workflowEventId, updateNodesMetadata, updateWorkflowEvent, refreshWorkflowEventDetails]);
};

export const useWorkflowPreviewWebSocket = (
  updateNodesMetadata: (changes: { nodeId: string; data: Partial<PreviewNodeData> }[]) => void,
  workflowEventId?: number | null,
  deps: DependencyList = [],
) => {
  useEffect(() => {
    let subscriptionId: string | null = null;

    if (workflowEventId) {
      subscriptionId = webSocketClient.subscribe({
        subscription: { eventType: SubscriptionTypeEnum.WorkflowActivity, variables: { workflowEventId } },
        onMessage: (message) => {
          if (message.workflowEventId === workflowEventId && message.activity) {
            updateNodesMetadata([{ nodeId: message.activity.identifier, data: { status: message.activity.status } }]);
          }
        },
      });
    }

    return () => {
      if (subscriptionId != undefined) {
        webSocketClient.unsubscribe(subscriptionId);
      }
    };
  }, [workflowEventId, updateNodesMetadata, ...deps]);
};
